import { useQuery } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';

/** Tabular section: columns and rows come from API so display can stay data-driven */
export type TabularSection = {
  title: string;
  columns: string[];
  rows: Record<string, unknown>[];
};

/** Top-line scores for a day: tabular (columns + rows) */
export type ScoresByDateResponse = {
  columns: string[];
  rows: Record<string, unknown>[];
};

/** Top-line scores for a league: tabular + pagination */
export type ScoresByLeagueResponse = {
  columns: string[];
  rows: Record<string, unknown>[];
  total: number;
  page: number;
  limit: number;
};

/** Top-line scores for a team: tabular + pagination */
export type ScoresByTeamResponse = {
  columns: string[];
  rows: Record<string, unknown>[];
  total: number;
  page: number;
  limit: number;
};

/** Box score or detailed scoring: array of tabular sections */
export type GameScoresSectionsResponse = {
  sections: TabularSection[];
  game?: Record<string, unknown> | null;
};

/**
 * Top-line scores for a single day (league + date).
 * Data is tabular (columns + rows) so the UI can render tables without hardcoding column names.
 */
export const useScoresByDate = (leagueId: number | string | undefined, date: string, enabled = true) => {
  return useQuery<ScoresByDateResponse>({
    queryKey: ['gameScores', 'byDate', leagueId, date],
    queryFn: async () => {
      if (!leagueId || !date) {
        return { columns: [], rows: [] };
      }
      const res = await apiClient.get('/games/scores', {
        params: { leagueId, date }
      });
      return res.data;
    },
    enabled: !!leagueId && !!date && enabled
  });
};

/**
 * Top-line scores for a league, paginated (most recent first).
 */
export const useScoresByLeague = (
  leagueId: number | string | undefined,
  page: number = 1,
  limit: number = 20,
  enabled = true
) => {
  return useQuery<ScoresByLeagueResponse>({
    queryKey: ['gameScores', 'byLeague', leagueId, page, limit],
    queryFn: async () => {
      if (!leagueId) {
        return { columns: [], rows: [], total: 0, page: 1, limit: 20 };
      }
      const res = await apiClient.get(`/games/scores/league/${leagueId}`, {
        params: { page, limit }
      });
      return res.data;
    },
    enabled: !!leagueId && enabled
  });
};

/**
 * Top-line scores for a team, paginated (20 per page, most recent first).
 * Data is tabular (columns + rows) + total/page/limit.
 */
export const useScoresByTeam = (
  teamId: number | string | undefined,
  page: number = 1,
  limit: number = 20,
  enabled = true
) => {
  return useQuery<ScoresByTeamResponse>({
    queryKey: ['gameScores', 'byTeam', teamId, page, limit],
    queryFn: async () => {
      if (!teamId) {
        return { columns: [], rows: [], total: 0, page: 1, limit: 20 };
      }
      const res = await apiClient.get(`/games/scores/team/${teamId}`, {
        params: { page, limit }
      });
      return res.data;
    },
    enabled: !!teamId && enabled
  });
};

/**
 * Box score for a game (top 5 sections: ScoreBoard, away batters, away pitchers, home batters, home pitchers).
 * Each section is tabular (title, columns, rows).
 */
export const useBoxScore = (gameId: number | string | undefined, enabled = true) => {
  return useQuery<GameScoresSectionsResponse>({
    queryKey: ['gameScores', 'box', gameId],
    queryFn: async () => {
      if (!gameId) {
        return { sections: [] };
      }
      const res = await apiClient.get(`/games/scores/game/${gameId}/box`);
      return res.data;
    },
    enabled: !!gameId && enabled
  });
};

/**
 * Detailed scoring for a game (ScoreBoard + detailed batters/pitchers + PlayByPlay).
 * Each section is tabular (title, columns, rows).
 */
export const useDetailedScoring = (gameId: number | string | undefined, enabled = true) => {
  return useQuery<GameScoresSectionsResponse>({
    queryKey: ['gameScores', 'detailed', gameId],
    queryFn: async () => {
      if (!gameId) {
        return { sections: [] };
      }
      const res = await apiClient.get(`/games/scores/game/${gameId}/detailed`);
      return res.data;
    },
    enabled: !!gameId && enabled
  });
};

/**
 * Alias used by admin code while migrating recap/broadcast UI to www.
 */
export const useGameDetails = useDetailedScoring;
